import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { AdvancedCommand, AdvancedCommandRequest, User } from "@vp/models";
import { NotificationService } from "@vp/shared/notification-service";
import { isNullOrUndefined } from "@vp/shared/utilities";
import { AdvancedCommandsService } from "@vp/virtual-care/data-access";
import { take } from "rxjs/operators";

interface AdvancedCommandDialogData {
  caseId: string | undefined;
  device: User | undefined;
  zoomCallActive: boolean | undefined;
}

@Component({
  selector: "vp-advanced-commands",
  templateUrl: "./advanced-commands.component.html",
  styleUrls: ["./advanced-commands.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdvancedCommandsComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _advancedCommandsService = inject(AdvancedCommandsService);
  private notificationService = inject(NotificationService);
  dialogRef = inject<MatDialogRef<AdvancedCommandsComponent>>(MatDialogRef, { optional: true });
  private data = inject<AdvancedCommandDialogData | undefined>(MAT_DIALOG_DATA, { optional: true });

  @Input() incomingCaseId: string | undefined;
  @Input() incomingDevice: User | undefined;

  caseId: string | undefined;
  device: User | undefined;
  zoomCallActive: boolean | undefined;
  listOfCommands: AdvancedCommand[] | undefined;

  ngOnInit(): void {
    if (!isNullOrUndefined(this.dialogRef) && !isNullOrUndefined(this.data)) {
      this.caseId = this.data.caseId;
      this.device = this.data.device;
      this.zoomCallActive = this.data.zoomCallActive;
    } else {
      this.caseId = this.incomingCaseId;
      this.device = this.incomingDevice;
    }

    this._advancedCommandsService
      .getDeviceCommands()
      .pipe(take(1))
      .subscribe(commands => {
        if (this.zoomCallActive) {
          commands = commands.filter(c => c.showWhenCallActive);
        } else {
          commands = commands.filter(c => c.showWhenCallInactive);
        }
        this.listOfCommands = commands.sort((a, b) => (a.methodName < b.methodName ? -1 : 1));
        this._changeDetectorRef.detectChanges();
      });
  }

  sendAdvancedCommand(command: AdvancedCommand): void {
    const sendCommand: AdvancedCommandRequest = {
      commandName: command.methodName,
      caseId: this.caseId !== "(No Assigned Value)" ? this.caseId : undefined,
      deviceUserId: this.device?.userId
    };

    this._advancedCommandsService
      .sendDeviceCommand(sendCommand)
      .pipe(take(1))
      .subscribe(() => {
        this.notificationService.successMessage(
          `Advanced Command '${command.displayName}' successfully sent`
        );
      });
  }

  public showAdvancedCommands(caseId: string, zoomCallActive: boolean): void {
    this.dialog.open(AdvancedCommandsComponent, {
      data: {
        caseId: caseId,
        device: this.device,
        zoomCallActive: zoomCallActive
      }
    });
  }
}
